import { environment } from '../environment/environment';

export interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  title: string;
  is_active: boolean;
  invited_by: string | null;
  created_at: string;
  user_role: {
    role_hierarchy: number | null;
  };
}

export const fetchUsers = async (): Promise<UserData[]> => {
  const token = localStorage.getItem('access_token');
  if (!token) return [];

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          query GetUsers {
            app_user(order_by: { created_at: desc }) {
              id
              name
              email
              role
              department
              title
              is_active
              invited_by
              created_at
              user_role {
                role_hierarchy
              }
            }
          }
        `,
      }),
    });

    const result = await response.json();

    if (result.errors) {
      console.error('Failed to fetch users:', result.errors);
      return [];
    }

    return result.data.app_user || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

export interface Role {
  name: string;
  display_name: string | null;
  description: string | null;
  role_hierarchy: number | null;
}

/** Fetches the live role catalog from `app.user_role` (name/display_name/description/role_hierarchy)
 * so the UI never hardcodes a role list — any role added to that table shows up automatically.
 * Accepts an optional token override for callers that hold a token that isn't the one in
 * localStorage (e.g. SharedOpportunity checking a decoded main-app token). */
export const fetchRoles = async (tokenOverride?: string): Promise<Role[]> => {
  const token = tokenOverride || localStorage.getItem('access_token');
  if (!token) return [];

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          query GetRoles {
            app_user_role(order_by: { role_hierarchy: asc }) {
              name
              display_name
              description
              role_hierarchy
            }
          }
        `,
      }),
    });

    const result = await response.json();

    if (result.errors) {
      console.error('Failed to fetch roles:', result.errors);
      return [];
    }

    return result.data?.app_user_role || [];
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [];
  }
};

export const changeUserRole = async (userId: string, newRole: string): Promise<{ success: boolean; error?: string; newRole?: string }> => {
  const token = localStorage.getItem('access_token');
  if (!token) return { success: false, error: 'Not authenticated' };

  if (!newRole) {
    return { success: false, error: 'Invalid role' };
  }

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          mutation ChangeUserRole($id: uuid!, $role: String!) {
            update_app_user_by_pk(pk_columns: { id: $id }, _set: { role: $role }) {
              id
              role
            }
          }
        `,
        variables: { id: userId, role: newRole },
      }),
    });

    const result = await response.json();
    if (result.errors) {
      console.error('Failed to change user role:', result.errors);
      return { success: false, error: 'Failed to change user role' };
    }
    if (!result.data?.update_app_user_by_pk) {
      console.error('Role change returned null — likely a Hasura permission issue', result);
      return { success: false, error: 'Permission denied. Check Hasura update permissions for the role column on app_user.' };
    }
    return { success: true, newRole };
  } catch (error) {
    console.error('Error changing user role:', error);
    return { success: false, error: 'Network error' };
  }
};

export const removeUser = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  const token = localStorage.getItem('access_token');
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          mutation RemoveUser($id: uuid!) {
            delete_app_user_by_pk(id: $id) {
              id
            }
          }
        `,
        variables: { id: userId },
      }),
    });

    const result = await response.json();
    if (result.errors) {
      console.error('Failed to remove user:', result.errors);
      return { success: false, error: 'Failed to remove user' };
    }
    return { success: true };
  } catch (error) {
    console.error('Error removing user:', error);
    return { success: false, error: 'Network error' };
  }
};

interface InviteUserInput {
  email: string;
  name: string;
  role: string;
  department?: string;
  title?: string;
  invited_by: string;
}

interface InviteUserResult {
  success: boolean;
  error?: string;
}

export const inviteUser = async (input: InviteUserInput): Promise<InviteUserResult> => {
  const token = localStorage.getItem('access_token');
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          mutation InviteUser($email: String!, $name: String!, $role: String!, $department: String, $title: String, $invited_by: uuid!) {
            insert_app_user_one(object: {
              email: $email,
              name: $name,
              role: $role,
              department: $department,
              title: $title,
              invited_by: $invited_by,
              is_active: false
            }) {
              id
            }
          }
        `,
        variables: {
          email: input.email,
          name: input.name,
          role: input.role,
          department: input.department || null,
          title: input.title || null,
          invited_by: input.invited_by,
        },
      }),
    });

    const result = await response.json();

    if (result.errors) {
      const errorMsg = result.errors[0]?.message || '';
      console.error('Failed to invite user:', result.errors);
      if (errorMsg.includes('Uniqueness violation') || errorMsg.includes('unique constraint')) {
        return { success: false, error: 'A user with this email already exists.' };
      }
      return { success: false, error: 'Something went wrong. Please try again.' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error inviting user:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};

interface UpdateUserProfileInput {
  id: string;
  name: string;
  title?: string;
  department?: string;
}

interface UpdateUserProfileResult {
  success: boolean;
  error?: string;
  user?: { id: string; name: string; title: string | null; department: string | null };
}

export const updateUserProfile = async (input: UpdateUserProfileInput): Promise<UpdateUserProfileResult> => {
  const token = localStorage.getItem('access_token');
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        query: `
          mutation UpdateUserProfile($id: uuid!, $name: String!, $title: String, $department: String) {
            update_app_user_by_pk(pk_columns: { id: $id }, _set: { name: $name, title: $title, department: $department }) {
              id
              name
              title
              department
            }
          }
        `,
        variables: {
          id: input.id,
          name: input.name,
          title: input.title || null,
          department: input.department || null,
        },
      }),
    });

    const result = await response.json();

    if (result.errors) {
      console.error('Failed to update profile:', result.errors);
      return { success: false, error: 'Failed to update profile. Please try again.' };
    }

    if (!result.data?.update_app_user_by_pk) {
      console.error('Profile update returned null — likely a Hasura permission issue', result);
      return { success: false, error: 'Permission denied. Check Hasura update permissions for the app_user table.' };
    }

    return { success: true, user: result.data.update_app_user_by_pk };
  } catch (error) {
    console.error('Error updating profile:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};
